import { __awaiter, __decorate, __extends, __generator } from "tslib";
import { _SharePointQueryableInstance, _SharePointQueryableCollection, spInvokableFactory, } from "../sharepointqueryable.js";
import { spPost } from "../operations.js";
import { odataUrlFrom } from "../odata.js";
import { extractWebUrl } from "../utils/extractweburl.js";
import { File } from "../files/types.js";
import { tag } from "../telemetry.js";
import { Web } from "../webs/index.js";
import "../items/index.js";
var _AppCatalog = /** @class */ (function (_super) {
    __extends(_AppCatalog, _super);
    function _AppCatalog(baseUrl, path) {
        var _this = _super.call(this, extractWebUrl(typeof baseUrl === "string" ? baseUrl : baseUrl.toUrl()), path) || this;
        // get the catalog path without the AvailableApps suffix
        // used for adding, syncing to teams
        var pathSplit = path.split("/");
        _this.catalogPath = pathSplit.slice(0, pathSplit.length - 1).join("/");
        return _this;
    }
    /**
     * Get details of specific app from the app catalog
     * @param id - Specify the guid of the app
     */
    _AppCatalog.prototype.getAppById = function (id) {
        return tag.configure(App(this, "getById('" + id + "')").configureFrom(this), "ac.getAppById");
    };
    /**
     * Synchronize a solution to the Microsoft Teams App Catalog
     * @param id - Specify the guid of the app
     * @param useSharePointItemId (optional) - By default this REST call requires the SP Item id of the app, not the app id.
     *                            PnPjs will try to fetch the item id by default, you can still use this parameter to pass your own item id in the first parameter
     */
    _AppCatalog.prototype.syncSolutionToTeams = function (id, useSharePointItemId) {
        if (useSharePointItemId === void 0) { useSharePointItemId = false; }
        return __awaiter(this, void 0, void 0, function () {
            var appId, webUrl, web, listId, listItems, poster;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        appId = null;
                        webUrl = extractWebUrl(this.toUrl());
                        if (!useSharePointItemId) return [3 /*break*/, 1];
                        appId = id;
                        return [3 /*break*/, 4];
                    case 1:
                        web = Web(webUrl).configureFrom(this);
                        return [4 /*yield*/, web.lists.select("Id").filter("EntityTypeName eq 'AppCatalog'")()];
                    case 2:
                        listId = (_a.sent())[0].Id;
                        return [4 /*yield*/, web.lists.getById(listId).items.filter("AppProductID eq '" + id + "'").top(1)()];
                    case 3:
                        listItems = _a.sent();
                        if (listItems && listItems.length > 0) {
                            appId = listItems[0].Id;
                        }
                        else {
                            throw Error("Did not find the app with id " + id + " in the appcatalog.");
                        }
                        _a.label = 4;
                    case 4:
                        poster = tag.configure(AppCatalog(webUrl, this.catalogPath + "/SyncSolutionToTeams(id=" + appId + ")"), "ac.syncSolutionToTeams");
                        poster.configureFrom(this);
                        return [4 /*yield*/, spPost(poster, {})];
                    case 5: return [2 /*return*/, _a.sent()];
                }
            });
        });
    };
    /**
     * Uploads an app package. Not supported for batching
     *
     * @param filename Filename to create.
     * @param content app package data (eg: the .app or .sppkg file).
     * @param shouldOverWrite Should an app with the same name in the same location be overwritten? (default: true)
     * @returns Promise<IAppAddResult>
     */
    _AppCatalog.prototype.add = function (filename, content, shouldOverWrite) {
        if (shouldOverWrite === void 0) { shouldOverWrite = true; }
        return __awaiter(this, void 0, void 0, function () {
            var adder, r;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        adder = tag.configure(AppCatalog(extractWebUrl(this.toUrl()), this.catalogPath + "/add(overwrite=" + shouldOverWrite + ",url='" + filename + "')"), "ac.add");
                        adder.configureFrom(this);
                        return [4 /*yield*/, spPost(adder, {
                                body: content, headers: {
                                    "binaryStringRequestBody": "true",
                                },
                            })];
                    case 1:
                        r = _a.sent();
                        return [2 /*return*/, {
                                data: r,
                                file: File(odataUrlFrom(r)),
                            }];
                }
            });
        });
    };
    return _AppCatalog;
}(_SharePointQueryableCollection));
export { _AppCatalog };
export var AppCatalog = spInvokableFactory(_AppCatalog);
var _App = /** @class */ (function (_super) {
    __extends(_App, _super);
    function _App() {
        return _super !== null && _super.apply(this, arguments) || this;
    }
    /**
     * This method deploys an app on the app catalog. It must be called in the context
     * of the tenant app catalog web or it will fail.
     *
     * @param skipFeatureDeployment Deploy the app to the entire tenant
     */
    _App.prototype.deploy = function (skipFeatureDeployment) {
        if (skipFeatureDeployment === void 0) { skipFeatureDeployment = false; }
        return this.do("Deploy(" + skipFeatureDeployment + ")");
    };
    /**
     * This method retracts a deployed app on the app catalog. It must be called in the context
     * of the tenant app catalog web or it will fail.
     */
    _App.prototype.retract = function () {
        return this.do("Retract");
    };
    /**
     * This method allows an app which is already deployed to be installed on a web
     */
    _App.prototype.install = function () {
        return this.do("Install");
    };
    /**
     * This method allows an app which is already installed to be uninstalled on a web
     * Note: when you use the REST API to uninstall a solution package from the site, it is not relocated to the recycle bin
     */
    _App.prototype.uninstall = function () {
        return this.do("Uninstall");
    };
    /**
     * This method allows an app which is already installed to be upgraded on a web
     */
    _App.prototype.upgrade = function () {
        return this.do("Upgrade");
    };
    /**
     * This method removes an app from the app catalog. It must be called in the context
     * of the tenant app catalog web or it will fail.
     */
    _App.prototype.remove = function () {
        return this.do("Remove");
    };
    _App.prototype.do = function (path) {
        return spPost(this.clone(App, path));
    };
    __decorate([
        tag("app.deploy")
    ], _App.prototype, "deploy", null);
    __decorate([
        tag("app.retract")
    ], _App.prototype, "retract", null);
    __decorate([
        tag("app.install")
    ], _App.prototype, "install", null);
    __decorate([
        tag("app.uninstall")
    ], _App.prototype, "uninstall", null);
    __decorate([
        tag("app.upgrade")
    ], _App.prototype, "upgrade", null);
    __decorate([
        tag("app.remove")
    ], _App.prototype, "remove", null);
    return _App;
}(_SharePointQueryableInstance));
export { _App };
export var App = spInvokableFactory(_App);
//# sourceMappingURL=types.js.map